"use strict";
/**
 * OPEN-VALUE SERVICES — the ₹1 convention, in one place.
 *
 * A large part of this hospital's catalogue cannot be priced in advance:
 * SPECIAL SURGERY, IMPLANTS, ANAESTHESIA CHARGES, ENT PROCEDURE, ward FOOD.
 * The legacy master expressed that by pricing them at ₹1 — a figure nobody ever
 * intended to collect, standing for "the counter enters the amount". 793 price
 * versions carry it today, across all four tiers (C1/C2/C3/C4).
 *
 * Until now the billing screens took that ₹1 literally: the service resolved,
 * the line said ₹1, and the operator had no way to enter what the case actually
 * cost. This module is the rule that fixes it, and it lives in `contracts` for
 * the same reason `charge-tier-pricing.ts` does — the BROWSER decides whether to
 * draw an editable Rate cell and the SERVER decides whether to accept the amount
 * typed into it, and those two must be one implementation. A second copy of
 * `=== 100` in the React grid is the specific defect this file prevents.
 *
 * WHAT THE SENTINEL COSTS, stated here rather than discovered later: a service
 * that genuinely costs ₹1 cannot be billed at ₹1 — it will always be treated as
 * open. That is the trade the convention makes, and it is why the rule is a
 * named constant with one home instead of a literal scattered through pricing.
 *
 * Amounts are integer paise throughout (ADR-043). Rupees exist only in the UI.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.OPEN_VALUE_RATE_MESSAGE = exports.OPEN_VALUE_SENTINEL_PAISE = void 0;
exports.isOpenValueRate = isOpenValueRate;
exports.isValidOpenValueRate = isValidOpenValueRate;
/**
 * ₹1, in paise. A price version at EXACTLY this value means "not priced —
 * ask the counter", never "one rupee".
 */
exports.OPEN_VALUE_SENTINEL_PAISE = 100;
/**
 * Does this RESOLVED rate mean the service is open-value?
 *
 * Takes the rate the resolver produced, not the charge: the same service can be
 * a real price under one payer's tariff and the sentinel under another, and
 * only the resolved figure knows which. Exact equality, deliberately — `<= 100`
 * would swallow a genuine ₹0.50 consumable and a ₹0 waiver into "type an
 * amount", and a zero-priced line is a different thing entirely.
 */
function isOpenValueRate(resolvedUnitRatePaise) {
    return resolvedUnitRatePaise === exports.OPEN_VALUE_SENTINEL_PAISE;
}
/**
 * Is `paise` an acceptable amount for the operator to have typed?
 *
 * Strictly ABOVE the sentinel, per the brief: "any C1/C2/C3/C4 amount should be
 * greater than ₹1". Re-entering ₹1 is not a decision, it is the placeholder
 * left untouched, and accepting it would post the very figure this whole
 * mechanism exists to stop reaching an invoice.
 */
function isValidOpenValueRate(paise) {
    return typeof paise === 'number' && Number.isInteger(paise) && paise > exports.OPEN_VALUE_SENTINEL_PAISE;
}
/** One wording for the rule, so the field hint and the server error agree. */
exports.OPEN_VALUE_RATE_MESSAGE = 'Enter an amount greater than ₹1 — this service is priced at the counter';
//# sourceMappingURL=open-value-pricing.js.map